import { FC, useMemo } from "react";
import {
  InteractiveContainer,
  PlainButton,
  Popover,
} from "@jugl-web/ui-components/cross-platform";
import { cx, useTranslations } from "@jugl-web/utils";
import { useEntityProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntityProvider";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";
import { clearMessageBody } from "@web-src/modules/chats/utils";
import { useClipboard } from "@jugl-web/utils/hooks/useClipboard";
import { ReactComponent as OptionsVertical } from "./assets/options-vertical.svg";
import { ReactComponent as CopyIcon } from "./assets/copy.svg";
import { ReactComponent as EditIcon } from "./assets/edit.svg";
import { ReactComponent as ForwardIcon } from "./assets/forward.svg";
import { ReactComponent as ReplyIcon } from "./assets/reply.svg";
import { ReactComponent as InfoIcon } from "./assets/info.svg";
import { ReactComponent as DeleteIcon } from "./assets/delete.svg";
import { ReactComponent as AddIcon } from "./assets/add.svg";
import { ReactComponent as PinIcon } from "./assets/pin.svg";
import { ReactComponent as EyesIcon } from "./assets/eyes.svg";
import { ChatMessageBubbleAction } from "../../types/ChatMessageBubbleAction";
import { useChatMessageBubble } from "../../providers/ChatMessageBubbleProvider/useChatMessageBubble";

export const ContextMenu: FC = () => {
  const { t } = useTranslations();
  const { copyToClipboard } = useClipboard();
  const { subscriptionRequiredAction } = useEntityProvider();
  const {
    isOutgoing,
    reactions,
    messageTextBody,
    triggerAction,
    triggerReaction,
    availableActions,
    customReactions,
  } = useChatMessageBubble();

  const myReactions = reactions?.myReactions || [];

  const reactionSet = useMemo(
    () => customReactions || ["😄", "👍", "👎", "😍", "✅"],
    [customReactions]
  );

  const items: {
    icon: React.ReactNode;
    text: string;
    onClick?: () => void;
    isHidden?: boolean;
  }[] = useMemo(
    () =>
      [
        {
          icon: <EditIcon />,
          text: t({
            id: "chats-page.edit-message",
            defaultMessage: "Edit Message",
          }),
          isHidden: !availableActions?.includes(ChatMessageBubbleAction.EDIT),
          onClick: () => triggerAction(ChatMessageBubbleAction.EDIT),
        },
        {
          icon: <ReplyIcon />,
          text: t({
            id: "common.reply",
            defaultMessage: `Reply`,
          }),
          isHidden: !availableActions?.includes(ChatMessageBubbleAction.REPLY),
          onClick: () => triggerAction(ChatMessageBubbleAction.REPLY),
        },
        {
          icon: <EyesIcon />,
          text: t({
            id: "chats-page.show-in-chat",
            defaultMessage: "Show in Chat",
          }),
          isHidden: !availableActions?.includes(ChatMessageBubbleAction.SHOW),
          onClick: () => triggerAction(ChatMessageBubbleAction.SHOW),
        },
        {
          icon: <PinIcon />,
          text: t({
            id: "chats-page.pin-message",
            defaultMessage: "Pin message",
          }),
          isHidden: !availableActions?.includes(ChatMessageBubbleAction.PIN),
          onClick: () => triggerAction(ChatMessageBubbleAction.PIN),
        },
        {
          icon: <PinIcon />,
          text: t({
            id: "chats-page.unpin-message",
            defaultMessage: "Unpin message",
          }),
          isHidden: !availableActions?.includes(ChatMessageBubbleAction.UNPIN),
          onClick: () => triggerAction(ChatMessageBubbleAction.UNPIN),
        },
        {
          icon: <CopyIcon />,
          text: t({
            id: "common.copy",
            defaultMessage: "Copy",
          }),
          isHidden: !availableActions?.includes(ChatMessageBubbleAction.COPY),
          onClick: () => {
            if (messageTextBody) {
              const clearText = clearMessageBody(
                messageTextBody.replace(
                  /^(\s*[-*+]|\d{1,9}[.)])\s*$/gm,
                  "$&\u200B\n"
                )
              );
              copyToClipboard(
                clearText,
                t({
                  id: "chats-page.message-copied",
                  defaultMessage: "Message copied to your clipboard",
                })
              );
            }
          },
        },
        {
          icon: <ForwardIcon />,
          text: t({
            id: "common.forward",
            defaultMessage: "Forward",
          }),
          isHidden: !availableActions?.includes(
            ChatMessageBubbleAction.FORWARD
          ),
          onClick: () => triggerAction(ChatMessageBubbleAction.FORWARD),
        },
        {
          icon: <InfoIcon />,
          onClick: () =>
            triggerAction(ChatMessageBubbleAction.SHOW_MESSAGE_INFO),
          isHidden: !availableActions?.includes(
            ChatMessageBubbleAction.SHOW_MESSAGE_INFO
          ),
          text: t({
            id: "chats-page.message-info",
            defaultMessage: "Message Info",
          }),
        },
        {
          icon: <DeleteIcon />,
          isHidden: !availableActions?.includes(ChatMessageBubbleAction.DELETE),
          text: t({
            id: "common.delete",
            defaultMessage: "Delete",
          }),
          onClick: () => triggerAction(ChatMessageBubbleAction.DELETE),
        },
      ].filter((item) => !item.isHidden),
    [availableActions, copyToClipboard, messageTextBody, t, triggerAction]
  );

  if (!availableActions?.length) return null;
  return (
    <Popover
      placement={isOutgoing ? "left-start" : "right-start"}
      renderTrigger={({ Trigger, triggerRef, isOpen }) => (
        <Trigger
          as="div"
          ref={triggerRef}
          className={cx(
            "absolute top-0 z-10 flex h-7 w-7 -translate-y-2 cursor-pointer items-center justify-center rounded-full bg-white opacity-0 shadow transition-all group-hover:opacity-100 hover:bg-gray-50",
            isOutgoing ? "left-0 -translate-x-2" : "right-0 translate-x-2",
            isOpen && "bg-gray-50 opacity-100"
          )}
        >
          <OptionsVertical />
        </Trigger>
      )}
      className="py-2"
    >
      {({ onClose, isOpen }) => (
        <div className={!isOpen ? "pointer-events-none" : undefined}>
          {items.map((item) => (
            <InteractiveContainer
              key={item.text}
              onClick={() => {
                if (item.onClick) {
                  subscriptionRequiredAction(item.onClick)();
                }
                onClose();
              }}
              className="flex h-full w-full min-w-[150px] items-center gap-2.5 px-4 py-2 hover:bg-grey-100"
            >
              {item.icon}
              <span className="grow text-left text-sm leading-[16px] text-dark">
                {item.text}
              </span>
            </InteractiveContainer>
          ))}
          {availableActions?.includes(ChatMessageBubbleAction.REACTION) && (
            <div
              className={cx(
                {
                  "mt-2 border-0 border-t border-solid border-gray-100 pt-2":
                    items.length,
                },
                "mx-2 flex items-center gap-1 text-[20px]"
              )}
            >
              {reactionSet.map((item) => (
                <PlainButton
                  key={item}
                  onClick={() => {
                    onClose();
                    triggerReaction(item);
                  }}
                  className={cx(
                    "h-8 w-8 cursor-pointer rounded border-none bg-transparent text-[20px] hover:bg-gray-100",
                    myReactions.includes(item) && "bg-gray-100"
                  )}
                >
                  {item}
                </PlainButton>
              ))}
              {!customReactions && (
                <Popover
                  placement="top"
                  renderTrigger={({ Trigger, triggerRef }) => (
                    <Trigger
                      as={PlainButton}
                      ref={triggerRef}
                      className="h-8 w-8 cursor-pointer rounded border-none bg-transparent text-[20px] hover:bg-gray-100"
                    >
                      <AddIcon />
                    </Trigger>
                  )}
                >
                  <Picker
                    theme="light"
                    data={data}
                    skinTonePosition="none"
                    previewPosition="none"
                    onEmojiSelect={(emoji: { [key: string]: string }) => {
                      triggerReaction(emoji.native);
                      onClose();
                    }}
                  />
                </Popover>
              )}
            </div>
          )}
        </div>
      )}
    </Popover>
  );
};
